import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import useAddressAutocompletePicker from '../../../hooks/useAddressAutocompletePicker';
import './index.scss';

const AddressAutocompleteInput = ({
    id,
    label = '',
    ariaLabel = '',
    placeholder = '',
    value = '',
    onChange = undefined,
    onSelect = undefined,
    disabled = false,
    error = '',
    countryCode = 'US',
    minChars = 3,
    debounceMs = 300,
    helpText = '',
    loadingText = 'Searching addresses…',
    emptyText = 'No matching addresses',
    autoComplete = 'off',
    required = false,
    preventEnterWithoutSelection = false,
    className = '',
}) => {
    const listboxId = `${id}-listbox`;

    const handleSelect = useCallback((geocoded, suggestion) => {
        if (typeof onChange === 'function' && geocoded?.label) {
            onChange(geocoded.label);
        }
        if (typeof onSelect === 'function') {
            onSelect(geocoded, suggestion);
        }
    }, [onChange, onSelect]);

    const {
        containerRef,
        inputValue,
        suggestions,
        isLoading,
        error: hookError,
        isOpen,
        highlightedIndex,
        setInputValue,
        setHighlightedIndex,
        openDropdown,
        pickSuggestion,
        handleKeyDown,
        handleBlur,
        showNoResults,
        getActiveDescendantId,
    } = useAddressAutocompletePicker({
        minChars,
        debounceMs,
        countryCode,
        limit: 8,
        enabled: !disabled,
        allowEmptyForwardFallback: true,
        preventEnterWithoutSelection,
        onSelect: handleSelect,
    });

    // Keep the controlled value in sync with the hook's internal value when
    // the parent resets it (e.g. after a successful submit). Never overwrite
    // an in-flight user query because the hook owns the current input.
    useEffect(() => {
        if (value === undefined || value === null) return;
        if (value === inputValue) return;
        // Suppress the lookup so opening with a prefilled value doesn't
        // immediately fire a network request.
        setInputValue(value, { suppressLookup: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleInputChange = (event) => {
        const nextValue = event.target.value;
        setInputValue(nextValue);
        if (typeof onChange === 'function') onChange(nextValue);
        // Typing invalidates any previously picked suggestion.
        if (typeof onSelect === 'function') onSelect(null);
    };

    const wrapperClassName = ['address-autocomplete-input', className].filter(Boolean).join(' ');
    const errorMessage = error || hookError?.message || null;
    const activeDescendant = getActiveDescendantId(id);

    return (
        <div className={wrapperClassName} ref={containerRef}>
            {label ? (
                <label htmlFor={id} className="address-autocomplete-input__label">
                    {label}
                    {required ? <span className="address-autocomplete-input__required" aria-hidden="true">*</span> : null}
                </label>
            ) : null}
            <input
                id={id}
                className="address-autocomplete-input__input"
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    if (suggestions.length > 0) openDropdown();
                }}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                role="combobox"
                aria-label={label ? undefined : ariaLabel}
                aria-autocomplete="list"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-activedescendant={activeDescendant}
                aria-required={required || undefined}
                aria-invalid={errorMessage ? 'true' : undefined}
                autoComplete={autoComplete}
            />
            {isLoading ? (
                <p className="address-autocomplete-input__hint" role="status">{loadingText}</p>
            ) : null}
            {showNoResults ? (
                <p className="address-autocomplete-input__hint" role="status">{emptyText}</p>
            ) : null}
            {!errorMessage && helpText && !isLoading && !showNoResults ? (
                <p className="address-autocomplete-input__hint">{helpText}</p>
            ) : null}
            {errorMessage ? (
                <p className="address-autocomplete-input__error" role="alert">{errorMessage}</p>
            ) : null}
            {isOpen && suggestions.length > 0 ? (
                <ul id={listboxId} className="address-autocomplete-input__list" role="listbox">
                    {suggestions.map((suggestion, index) => {
                        const isHighlighted = index === highlightedIndex;
                        return (
                            <li key={`${suggestion.id || suggestion.label}-${index}`} role="presentation">
                                <button
                                    id={`${id}-option-${index}`}
                                    type="button"
                                    role="option"
                                    aria-selected={isHighlighted}
                                    className={`address-autocomplete-input__option${isHighlighted ? ' is-highlighted' : ''}`}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        pickSuggestion(suggestion);
                                    }}
                                    onClick={(event) => {
                                        // Pointer selection already happens on mouse down so
                                        // the input doesn't blur before we can pick. Keyboard
                                        // and assistive-tech activation synthesize a click with
                                        // detail === 0, so handle that path here.
                                        if (event.detail !== 0) {
                                            return;
                                        }
                                        pickSuggestion(suggestion);
                                    }}
                                >
                                    {suggestion.label}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
};

AddressAutocompleteInput.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onSelect: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    countryCode: PropTypes.string,
    minChars: PropTypes.number,
    debounceMs: PropTypes.number,
    helpText: PropTypes.string,
    loadingText: PropTypes.string,
    emptyText: PropTypes.string,
    autoComplete: PropTypes.string,
    required: PropTypes.bool,
    preventEnterWithoutSelection: PropTypes.bool,
    className: PropTypes.string,
};

export default AddressAutocompleteInput;
